import type { CSSProperties } from 'react';

// Native <select> popups can't be styled/scroll-limited by CSS - this is
// the option-row height used to size the custom dropdown's scroll area.
const DROPDOWN_OPTION_HEIGHT_PX = 32;
const DROPDOWN_VISIBLE_OPTIONS = 6;

// Backstage UI design tokens (from @backstage/ui) instead of hardcoded
// hex colors - these are CSS custom properties set on :root and flipped by
// [data-theme-mode], so this table follows the app's light/dark theme
// automatically instead of always rendering as a white light-mode table.
// Custom properties inherit through shadow DOM boundaries (unlike normal
// style rules), so this works even though the addon mounts inside the
// TechDocs page's shadow root - same reasoning as resizeHandle's
// `currentColor` below. Each has a literal fallback matching the original
// hardcoded value, so nothing changes if these tokens are ever unavailable.
const SURFACE_BG = 'var(--bui-bg-neutral-1, #fff)';
const TEXT_PRIMARY = 'var(--bui-fg-primary, #111)';
const TEXT_SECONDARY = 'var(--bui-fg-secondary, #666)';
const TEXT_INFO = 'var(--bui-fg-announcement, #1976d2)';
const BORDER_1 = 'var(--bui-border-1, #ccc)';
const BORDER_2 = 'var(--bui-border-2, #ccc)';
const ROW_BORDER = 'var(--bui-border-1, #d0d0d0)';
// --bui-radius-2 is exactly 4px, the only radius value this file uses that
// falls exactly on Backstage UI's token scale - a safe, zero-visual-change
// swap. The rest of this file's spacing (gap/padding) intentionally uses a
// denser scale (3px/6px/10px increments) than the 4px-based --bui-space-*
// tokens, chosen for this compact table UI - adopting those tokens would
// visibly loosen the layout, so they're left as literals rather than
// force-fit onto a coarser grid.
const RADIUS_SM = 'var(--bui-radius-2, 4px)';

export const styles: Record<string, CSSProperties> = {
  wrapper: { fontFamily: 'inherit', margin: '1em 0', position: 'relative' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
  },
  searchIcon: {
    position: 'absolute',
    top: '50%',
    right: 10,
    transform: 'translateY(-50%)',
    opacity: 0.5,
    pointerEvents: 'none',
    display: 'flex',
  },
  filterChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 6,
    marginBottom: 8,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    fontSize: 12,
    fontWeight: 600,
  },
  chipRemove: {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    fontSize: 13,
    lineHeight: 1,
    padding: 0,
  },
  clearAllLink: {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    color: TEXT_INFO,
    fontSize: 12,
    fontWeight: 600,
    padding: 0,
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    cursor: 'pointer',
    fontSize: 14,
    whiteSpace: 'nowrap',
  },
  filterButtonActive: {
    // Overrides the same `border` shorthand filterButton uses (not the
    // longhand `borderColor`) - mixing shorthand and longhand for the same
    // property across a style spread makes React warn about unreliable
    // removal semantics on re-render.
    border: `1px solid ${TEXT_SECONDARY}`,
    fontWeight: 600,
  },
  search: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px 34px 6px 10px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    fontSize: 14,
  },
  filterPanel: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    zIndex: 10,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    border: `1px solid ${BORDER_1}`,
    borderRadius: 6,
    // No stable (non-deprecated) BUI shadow token exists yet - see
    // @backstage/no-deprecated-bui-tokens - so this stays a literal value
    // rather than wiring to one that's already scheduled for removal.
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    padding: 12,
    minWidth: 260,
    maxWidth: 340,
  },
  filterPanelEmpty: {
    fontSize: 13,
    color: TEXT_SECONDARY,
    fontStyle: 'italic',
  },
  filterPanelRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 10,
  },
  filterPanelLabel: {
    fontSize: 12,
    fontWeight: 600,
  },
  dropdown: { position: 'relative' },
  dropdownButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    cursor: 'pointer',
    fontSize: 13,
  },
  dropdownList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 2,
    zIndex: 20,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_SM,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    maxHeight: DROPDOWN_OPTION_HEIGHT_PX * DROPDOWN_VISIBLE_OPTIONS,
    overflowY: 'auto',
  },
  dropdownOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dropdownOptionLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tableScroll: { overflowX: 'auto' },
  table: {
    display: 'table',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  },
  headerGroup: { display: 'table-header-group' },
  bodyGroup: { display: 'table-row-group' },
  row: { display: 'table-row' },
  headerCell: {
    position: 'relative',
    display: 'table-cell',
    padding: '8px 12px',
    borderBottom: `2px solid ${BORDER_2}`,
    textAlign: 'left',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: 8,
    cursor: 'col-resize',
    userSelect: 'none',
    zIndex: 2,
    // currentColor inherits whatever text color the host page is already
    // using (black in light mode, white in dark mode) instead of a
    // hardcoded shade, so it never needs its own background to stay
    // legible - opacity just softens it into a subtle line either way.
    borderRight: '2px solid currentColor',
    opacity: 0.3,
  },
  headerLabel: {
    // Reset native <button> browser chrome (border, background, padding,
    // font) - this is a real <button> for accessibility, but needs to look
    // and lay out like the plain clickable label it replaced.
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    display: 'block',
    width: '100%',
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 600,
  },
  rowNumberHeaderCell: {
    display: 'table-cell',
    padding: '8px 12px',
    borderBottom: `2px solid ${BORDER_2}`,
    textAlign: 'right',
    verticalAlign: 'top',
    fontWeight: 600,
    opacity: 0.7,
  },
  rowNumberCell: {
    display: 'table-cell',
    padding: '8px 12px',
    borderBottom: `1px solid ${ROW_BORDER}`,
    textAlign: 'right',
    opacity: 0.7,
  },
  cell: {
    display: 'table-cell',
    padding: '8px 12px',
    borderBottom: `1px solid ${ROW_BORDER}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0,
  },
  emptyState: { padding: '12px 0', color: TEXT_SECONDARY, fontStyle: 'italic' },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
    fontSize: 14,
  },
  pageButton: {
    padding: '4px 10px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    cursor: 'pointer',
    fontSize: 14,
  },
  pageButtonDisabled: {
    cursor: 'default',
    opacity: 0.5,
  },
  pageSizeSelect: {
    padding: '4px 6px',
    borderRadius: RADIUS_SM,
    border: `1px solid ${BORDER_1}`,
    background: SURFACE_BG,
    color: TEXT_PRIMARY,
    fontSize: 14,
  },
};
